<template>
  <v-card class="roster-box" flat>
    <header class="roster-header">
      <div class="roster-header-main">
        <div class="roster-eyebrow">Kokoonpano</div>
        <h3 class="roster-title">Yleisin kokoonpano</h3>
        <div v-if="primaryRoster" class="roster-subtitle">
          {{ summaryLabel }}
        </div>
      </div>

      <div v-if="primaryRoster" class="roster-stat">
        <div class="roster-stat-value">{{ primarySharePercent }}%</div>
        <div class="roster-stat-label">{{ primaryGamesLabel }}</div>
      </div>
    </header>

    <div class="roster-body">
      <div v-if="!canFetch" class="roster-empty">
        Valitse joukkue ja kausi.
      </div>

      <div v-else-if="loading" class="roster-loading">
        <div v-for="i in 12" :key="i" class="skeleton-row" />
      </div>

      <v-alert v-else-if="error" type="error" dense outlined class="roster-error">
        {{ error }}
      </v-alert>

      <div v-else-if="!primaryRoster" class="roster-empty">
        Ei kokoonpanodataa valituilla suodattimilla.
      </div>

      <table v-else class="roster-table">
        <thead>
          <tr>
            <th class="col-num">#</th>
            <th class="col-pos">Pos</th>
            <th class="col-player">Pelaaja</th>
            <th
              v-for="state in baseStates"
              :key="`th-${state.value}`"
              class="col-state"
            >
              {{ state.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="player in primaryRoster.players"
            :key="`primary-${player.num}-${player.player_id}`"
          >
            <td class="col-num">{{ player.num }}</td>
            <td class="col-pos">
              <span class="pos-pill">{{ player.pos || '–' }}</span>
            </td>
            <td class="col-player">
              <div class="player-cell">
                <div class="player-avatar">
                  <img
                    v-if="player.picture_url"
                    :src="player.picture_url"
                    :alt="player.player_name"
                  />
                  <span v-else class="player-initials">
                    {{ initials(player.player_name) }}
                  </span>
                </div>
                <div class="player-text">
                  <div class="player-name">{{ player.player_name }}</div>
                  <div
                    v-if="player.alternates && player.alternates.length"
                    class="player-alternates"
                  >
                    <span
                      v-for="alt in player.alternates"
                      :key="`alt-${player.num}-${alt.player_id}`"
                      class="alternate"
                    >
                      {{ alt.player_name }}<span class="alternate-count">{{ alt.games_count }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </td>
            <td
              v-for="state in baseStates"
              :key="`td-${player.player_id}-${state.value}`"
              class="col-state"
            >
              <button
                v-tooltip="`Suodatus: &quot;${player.player_name} - ${state.label} til&quot;`"
                type="button"
                class="state-btn"
                :disabled="!canFilter"
                @click="openFilter(player, state.value)"
              >
                <v-icon size="14">mdi-play</v-icon>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <section v-if="primaryRoster && otherPlayers.length" class="roster-others">
      <div class="roster-others-header">
        <span class="roster-eyebrow">Muut pelaajat</span>
        <span class="roster-others-count">{{ otherPlayers.length }}</span>
      </div>

      <table class="roster-table roster-others-table">
        <thead>
          <tr>
            <th class="col-games">Pelit</th>
            <th class="col-player">Pelaaja</th>
            <th
              v-for="state in baseStates"
              :key="`other-th-${state.value}`"
              class="col-state"
            >
              {{ state.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="player in otherPlayers"
            :key="`other-${player.player_id}`"
          >
            <td class="col-games">{{ player.games_count }}</td>
            <td class="col-player">
              <div class="player-cell">
                <div class="player-avatar player-avatar-sm">
                  <img
                    v-if="player.picture_url"
                    :src="player.picture_url"
                    :alt="player.player_name"
                  />
                  <span v-else class="player-initials">
                    {{ initials(player.player_name) }}
                  </span>
                </div>
                <div class="player-text">
                  <div class="player-name">{{ player.player_name }}</div>
                </div>
              </div>
            </td>
            <td
              v-for="state in baseStates"
              :key="`other-td-${player.player_id}-${state.value}`"
              class="col-state"
            >
              <button
                v-tooltip="`Suodatus: &quot;${player.player_name} - ${state.label} til&quot;`"
                type="button"
                class="state-btn"
                :disabled="!canFilter"
                @click="openFilter(player, state.value)"
              >
                <v-icon size="14">mdi-play</v-icon>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import ax from '@/utils/axios/axios'
import { buildFilterUrl } from '@/utils/joukkueet/filterUrl'

export default {
  name: 'MostCommonRosterBox',
  props: {
    leagueId: {
      type: Number,
      default: null
    },
    leagueClubId: {
      type: Number,
      default: null
    },
    seasonId: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    roster: null,
    loading: false,
    error: null,
    requestId: 0,
    baseStates: [
      { value: '0', label: '0' },
      { value: '1', label: '1' },
      { value: '1-2', label: '2' },
      { value: 'Ajo', label: 'Ajo' }
    ]
  }),
  computed: {
    ...mapGetters('user', [
      'currentTeamId'
    ]),
    canFetch() {
      return Boolean(this.leagueId && this.leagueClubId && this.seasonId)
    },
    canFilter() {
      return Boolean(this.leagueId && this.seasonId && this.currentTeamId)
    },
    requestKey() {
      return `${this.leagueId || ''}:${this.leagueClubId || ''}:${this.seasonId || ''}`
    },
    primaryRoster() {
      return this.roster?.primary_roster || null
    },
    otherPlayers() {
      return this.roster?.other_players || []
    },
    primarySharePercent() {
      const share = this.primaryRoster?.share || 0
      return Math.round(share * 100)
    },
    primaryGamesLabel() {
      if(!this.primaryRoster || !this.roster) return ''
      const total = this.roster.games_with_rosters || 0
      return `${this.primaryRoster.games_count} / ${total} ottelua`
    },
    summaryLabel() {
      if(!this.roster) return ''

      const fullRosters = this.roster.games_with_rosters || 0
      const games = this.roster.games_count || 0
      const incomplete = this.roster.games_without_full_roster || 0

      if(!games) return ''

      const incompleteText = incomplete
        ? ` · ${incomplete} ottelua ilman täyttä kokoonpanoa`
        : ''

      return `Täysi kokoonpano ${fullRosters} / ${games} ottelussa${incompleteText}`
    }
  },
  watch: {
    requestKey: {
      immediate: true,
      handler() {
        this.refresh()
      }
    }
  },
  methods: {
    refresh() {
      if(!this.canFetch) {
        this.roster = null
        this.error = null
        this.loading = false
        return
      }

      const requestId = this.requestId + 1
      this.requestId = requestId
      this.loading = true
      this.error = null

      ax.get(`/league/${this.leagueId}/rosters`, {
        params: {
          season_id: this.seasonId,
          league_club_id: this.leagueClubId
        }
      })
        .then(res => {
          if(requestId !== this.requestId) return
          this.roster = res.data
        })
        .catch(err => {
          if(requestId !== this.requestId) return
          this.roster = null
          this.error = err.response?.data?.error || 'Kokoonpanon haku epäonnistui'
        })
        .finally(() => {
          if(requestId === this.requestId) this.loading = false
        })
    },
    initials(name) {
      if(!name) return ''
      return name
        .split(/\s+/)
        .filter(Boolean)
        .map(part => part[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    },
    openFilter(player, baseState) {
      if(!this.canFilter) return

      const url = buildFilterUrl({
        season: this.seasonId,
        hitters: [player.player_id],
        bases_state: [baseState],
        league_id: this.leagueId,
        team_id: this.currentTeamId
      })

      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }
}
</script>

<style scoped>
.roster-box {
  margin-top: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
}

.roster-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.roster-header-main {
  min-width: 0;
}

.roster-eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 4px;
}

.roster-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}

.roster-subtitle {
  margin-top: 6px;
  font-size: 13px;
  color: #6b7280;
}

.roster-stat {
  flex-shrink: 0;
  text-align: right;
  padding: 6px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
}

.roster-stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.roster-stat-label {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}

.roster-body {
  padding: 4px 0;
}

.roster-empty {
  padding: 32px 24px;
  font-size: 14px;
  color: #6b7280;
  text-align: center;
}

.roster-loading {
  padding: 12px 24px 16px;
}

.skeleton-row {
  height: 44px;
  margin: 6px 0;
  border-radius: 6px;
  background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s infinite;
}

@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.roster-error {
  margin: 16px 24px;
}

.roster-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.roster-table thead th {
  padding: 10px 16px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  text-align: left;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.roster-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 120ms ease;
}

.roster-table tbody tr:last-child {
  border-bottom: none;
}

.roster-table tbody tr:hover {
  background: #fafafa;
}

.roster-table td {
  padding: 12px 16px;
  vertical-align: middle;
  color: #111827;
}

.col-num {
  width: 48px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #6b7280;
  text-align: center;
}

.col-pos {
  width: 72px;
}

.col-player {
  min-width: 0;
}

.col-state {
  width: 52px;
  text-align: center;
}

.roster-table thead th.col-state {
  text-align: center;
}

.state-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease, border-color 120ms ease;
}

.state-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #e5e7eb;
  color: #111827;
}

.state-btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.pos-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  padding: 3px 8px;
  border-radius: 6px;
  background: #f3f4f6;
  color: #374151;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.player-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.player-avatar {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e5e7eb;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.player-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.player-avatar-sm {
  width: 28px;
  height: 28px;
}

.player-initials {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.02em;
}

.player-avatar-sm .player-initials {
  font-size: 11px;
}

.player-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.player-name {
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.player-alternates {
  margin-top: 2px;
  font-size: 11px;
  color: #9ca3af;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alternate {
  display: inline;
}

.alternate + .alternate::before {
  content: ' · ';
  color: #d1d5db;
}

.alternate-count {
  margin-left: 4px;
  color: #b7bcc4;
  font-variant-numeric: tabular-nums;
}

.roster-others {
  border-top: 1px solid #e5e7eb;
  background: #fafafa;
  padding-bottom: 4px;
}

.roster-others-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px 8px;
}

.roster-others-header .roster-eyebrow {
  margin-bottom: 0;
}

.roster-others-count {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  font-variant-numeric: tabular-nums;
}

.roster-others-table thead th {
  background: transparent;
}

.roster-others-table tbody tr:hover {
  background: #f3f4f6;
}

.roster-others-table td {
  padding: 8px 16px;
  font-size: 13px;
}

.col-games {
  width: 56px;
  font-weight: 700;
  color: #374151;
  font-variant-numeric: tabular-nums;
  text-align: center;
}

.roster-table thead th.col-games {
  text-align: center;
}
</style>
